import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import CardNav from './CardNav';
import Hyperspeed from './Hyperspeed';
import '../App.css';

function JoinGamePage({ joinCode, onJoinSuccess, onFindImageGame }) {
  const { user, userProfile, signIn, signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [game, setGame] = useState(null);
  const [checkingCode, setCheckingCode] = useState(true);
  const [autoJoining, setAutoJoining] = useState(false);

  useEffect(() => {
    if (joinCode) {
      checkJoinCode();
    }
  }, [joinCode]);

  // Auto-join if user is already authenticated
  useEffect(() => {
    const shouldAutoJoin = game && user && userProfile && !autoJoining && !loading && !error;
    if (shouldAutoJoin) {
      console.log('[JoinGamePage] User is authenticated, auto-joining game...');
      handleAutoJoin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.id, user?.id, userProfile?.id]);

  const checkJoinCode = async () => {
    try {
      console.log('[JoinGamePage] Checking join code:', joinCode);
      
      // Normalize join code to uppercase (join codes are stored uppercase)
      const normalizedCode = joinCode.trim().toUpperCase();
      
      // Try multiple query approaches to work around potential RLS/406 issues
      let gameData = null;
      let queryError = null;
      
      // Approach 1: Standard query with maybeSingle
      console.log('[JoinGamePage] Attempting standard query...');
      const { data: data1, error: error1 } = await supabase
        .from('games')
        .select('id, game_name, game_status, join_code')
        .eq('join_code', normalizedCode)
        .maybeSingle();
      
      if (!error1 && data1) {
        gameData = data1;
        console.log('[JoinGamePage] Standard query succeeded');
      } else if (error1) {
        queryError = error1;
        console.log('[JoinGamePage] Standard query failed:', error1);
        
        // Approach 2: Try without maybeSingle (use limit instead)
        if (error1.status === 406 || error1.message?.includes('406')) {
          console.log('[JoinGamePage] Got 406, trying query with limit...');
          const { data: data2, error: error2 } = await supabase
            .from('games')
            .select('id, game_name, game_status, join_code')
            .eq('join_code', normalizedCode)
            .limit(1);
          
          if (!error2 && data2 && data2.length > 0) {
            gameData = data2[0];
            console.log('[JoinGamePage] Limit query succeeded');
          } else if (error2) {
            queryError = error2;
            console.log('[JoinGamePage] Limit query also failed:', error2);
            
            // Approach 3: Try fetching all published/started games and filter client-side
            console.log('[JoinGamePage] Trying fetch-all approach...');
            const { data: allGames, error: allError } = await supabase
              .from('games')
              .select('id, game_name, game_status, join_code')
              .in('game_status', ['published', 'started']);
            
            if (!allError && allGames) {
              const foundGame = allGames.find(g => 
                g.join_code && g.join_code.toUpperCase() === normalizedCode
              );
              if (foundGame) {
                gameData = foundGame;
                console.log('[JoinGamePage] Fetch-all approach succeeded');
              } else {
                console.log('[JoinGamePage] Join code not found in published/started games');
              }
            } else if (allError) {
              queryError = allError;
              console.log('[JoinGamePage] Fetch-all also failed:', allError);
            }
          }
        }
      }
      
      // Handle results
      if (queryError && !gameData) {
        console.error('[JoinGamePage] All query approaches failed. Last error:', queryError);
        setError(`Unable to verify join code. ${queryError.message || 'Please check your connection and try again.'}`);
        setCheckingCode(false);
        return;
      }
      
      if (!gameData) {
        // Check if it's a Find The Image game code
        console.log('[JoinGamePage] Not found in games table, checking find_image_games...');
        const { data: fiGame, error: fiError } = await supabase
          .from('find_image_games')
          .select('id, game_name, game_status, join_code')
          .eq('join_code', normalizedCode)
          .maybeSingle();

        if (!fiError && fiGame) {
          console.log('[JoinGamePage] Found Find The Image game:', fiGame);
          if (onFindImageGame) {
            onFindImageGame(fiGame.join_code);
          }
          setCheckingCode(false);
          return;
        }

        setError('Invalid join code. Please check and try again.');
        setCheckingCode(false);
        return;
      }

      if (gameData.game_status !== 'started' && gameData.game_status !== 'published') {
        setError('This game is not available to join yet.');
        setCheckingCode(false);
        return;
      }

      console.log('[JoinGamePage] Join code verified successfully:', gameData);
      setGame(gameData);
      setCheckingCode(false);
    } catch (err) {
      console.error('[JoinGamePage] Exception checking join code:', err);
      setError(`Failed to verify join code: ${err.message || 'Please try again.'}`);
      setCheckingCode(false);
    }
  };

  const handleAutoJoin = async () => {
    if (!user || !userProfile || !game) return;
    
    setAutoJoining(true);
    setLoading(true);
    setError('');

    try {
      // Add user to game_participants
      const { error: participantError } = await supabase
        .from('game_participants')
        .insert({
          game_id: game.id,
          user_id: user.id
        });

      if (participantError && participantError.code !== '23505') { // Ignore duplicate entry
        throw participantError;
      }

      console.log('[JoinGamePage] Successfully joined game as authenticated user');
      
      // Fetch full game object for ActiveGameView
      const { data: fullGame, error: gameError } = await supabase
        .from('games')
        .select('*')
        .eq('id', game.id)
        .single();

      if (gameError) {
        console.error('[JoinGamePage] Error fetching full game:', gameError);
        // Still proceed with the basic game object
      }
      
      // Success - redirect to game
      if (onJoinSuccess) {
        onJoinSuccess(fullGame || game);
      } else {
        // Fallback: reload page to get authenticated state
        window.location.href = `/game/${game.id}`;
      }
    } catch (err) {
      console.error('[JoinGamePage] Error auto-joining game:', err);
      setError(err.message || 'Failed to join game. Please try again.');
      setLoading(false);
      setAutoJoining(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email.trim()) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    if (!name.trim()) {
      setError('Please enter your name');
      setLoading(false);
      return;
    }

    try {
      // Try to sign up first (will work for new users)
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: email.toLowerCase(),
        password: Math.random().toString(36).slice(-12) + Math.random().toString(36).slice(-12) + 'A1!', // Strong random password
        options: {
          data: {
            full_name: name.trim(),
            role: 'player'
          },
          emailRedirectTo: `${window.location.origin}/join/${joinCode}`
        }
      });

      let userId;

      if (signUpError) {
        // If user already exists, join instantly with email/name (no authentication required)
        if (signUpError.message.includes('already registered') || signUpError.message.includes('already exists')) {
          console.log('[JoinGamePage] User already exists, joining with email/name only');
          
          // Join instantly with email and name (no authentication needed)
          const { error: participantError } = await supabase
            .from('game_participants')
            .insert({
              game_id: game.id,
              email: email.toLowerCase(),
              player_name: name.trim(),
              user_id: null // No user_id for instant join
            });

          if (participantError) {
            // If duplicate entry, check if it's because they already joined
            if (participantError.code === '23505') {
              console.log('[JoinGamePage] User already in game, proceeding...');
            } else {
              throw participantError;
            }
          }

          // Success - join instantly without authentication
          console.log('[JoinGamePage] Successfully joined game as email-only participant');
          if (onJoinSuccess) {
            onJoinSuccess(game);
          } else {
            window.location.href = `/game/${game.id}`;
          }
          return;
        } else {
          throw signUpError;
        }
      }

      if (!signUpData.user) {
        throw new Error('Failed to create account');
      }

      userId = signUpData.user.id;

      // Update user profile with name
      await supabase
        .from('user_profiles')
        .update({ full_name: name.trim() })
        .eq('id', userId);

      // Add user to game_participants (with user_id for authenticated users)
      const { error: participantError } = await supabase
        .from('game_participants')
        .insert({
          game_id: game.id,
          user_id: userId,
          email: email.toLowerCase(), // Also store email for consistency
          player_name: name.trim() // Also store name for consistency
        });

      if (participantError && participantError.code !== '23505') { // Ignore duplicate entry
        throw participantError;
      }

      // Check if email confirmation is required
      if (signUpData.user && !signUpData.session) {
        // Email confirmation required - but still allow them to join with email/name
        console.log('[JoinGamePage] Email confirmation required, joining with email/name only');
        
        // Join with email/name while they wait for confirmation
        const { error: emailOnlyError } = await supabase
          .from('game_participants')
          .insert({
            game_id: game.id,
            email: email.toLowerCase(),
            player_name: name.trim(),
            user_id: null
          });

        if (emailOnlyError && emailOnlyError.code !== '23505') {
          console.error('[JoinGamePage] Error joining with email:', emailOnlyError);
        }

        // Still redirect them to game (they can view, but may need to authenticate for submissions)
        if (onJoinSuccess) {
          onJoinSuccess(game);
        } else {
          window.location.href = `/game/${game.id}`;
        }
        return;
      }

      // Success - user is authenticated, redirect to game
      if (onJoinSuccess) {
        onJoinSuccess(game);
      } else {
        // Fallback: reload page to get authenticated state
        window.location.href = `/game/${game.id}`;
      }
    } catch (err) {
      console.error('Error joining game:', err);
      setError(err.message || 'Failed to join game. Please try again.');
      setLoading(false);
    }
  };

  if (checkingCode) {
    return (
      <>
        <CardNav
          logo="/rollerCoaster_logo.svg"
          logoAlt="Client Roller Coaster Logo"
          logoText="Client Roller Coaster"
          baseColor="#242323"
          menuColor="#fff"
          showBackButton={false}
          showHamburger={false}
          showButton={false}
          items={[]}
        />
        {/* Animated Background */}
        <div 
          className="hyperspeed-animation-container"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            overflow: 'hidden',
            background: '#000',
            zIndex: 0
          }}
        >
          <Hyperspeed 
            effectOptions={{
              distortion: 'turbulentDistortion',
              length: 400,
              roadWidth: 10,
              islandWidth: 2,
              lanesPerRoad: 4,
              fovSpeedUp: 150,
              speedUp: 0.5,
              fov: 70,
              carLightsFade: 0.4,
              totalSideLightSticks: 20,
              lightPairsPerRoadWay: 40,
              shoulderLinesWidthPercentage: 0.05,
              brokenLinesWidthPercentage: 0.1,
              brokenLinesLengthPercentage: 0.5,
              lightStickWidth: [0.12, 0.5],
              lightStickHeight: [1.3, 1.7],
              movingAwaySpeed: [15, 20],
              movingCloserSpeed: [-30, -40],
              carLightsLength: [400 * 0.03, 400 * 0.2],
              carLightsRadius: [0.05, 0.14],
              carWidthPercentage: [0.3, 0.5],
              carShiftX: [-0.8, 0.8],
              carFloorSeparation: [0, 5],
              colors: {
                roadColor: 0x080808,
                islandColor: 0x0a0a0a,
                background: 0x000000,
                shoulderLines: 0xffffff,
                brokenLines: 0xffffff,
                leftCars: [0xd856bf, 0x6750a2, 0xc247ac],
                rightCars: [0x03b3c3, 0x0e5ea5, 0x324555],
                sticks: 0x03b3c3
              }
            }}
          />
        </div>
        <div style={{
          position: 'relative',
          minHeight: '100vh',
          paddingTop: 'calc(2em + 60px + 2rem)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          zIndex: 1
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Verifying join code...</div>
          </div>
        </div>
      </>
    );
  }

  if (!game) {
    return (
      <>
        <CardNav
          logo="/rollerCoaster_logo.svg"
          logoAlt="Client Roller Coaster Logo"
          logoText="Client Roller Coaster"
          baseColor="#242323"
          menuColor="#fff"
          showBackButton={false}
          showHamburger={false}
          showButton={false}
          items={[]}
        />
        {/* Animated Background */}
        <div 
          className="hyperspeed-animation-container"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            overflow: 'hidden',
            background: '#000',
            zIndex: 0
          }}
        >
          <Hyperspeed 
            effectOptions={{
              distortion: 'turbulentDistortion',
              length: 400,
              roadWidth: 10,
              islandWidth: 2,
              lanesPerRoad: 4,
              fovSpeedUp: 150,
              speedUp: 0.5,
              fov: 70,
              carLightsFade: 0.4,
              totalSideLightSticks: 20,
              lightPairsPerRoadWay: 40,
              shoulderLinesWidthPercentage: 0.05,
              brokenLinesWidthPercentage: 0.1,
              brokenLinesLengthPercentage: 0.5,
              lightStickWidth: [0.12, 0.5],
              lightStickHeight: [1.3, 1.7],
              movingAwaySpeed: [15, 20],
              movingCloserSpeed: [-30, -40],
              carLightsLength: [400 * 0.03, 400 * 0.2],
              carLightsRadius: [0.05, 0.14],
              carWidthPercentage: [0.3, 0.5],
              carShiftX: [-0.8, 0.8],
              carFloorSeparation: [0, 5],
              colors: {
                roadColor: 0x080808,
                islandColor: 0x0a0a0a,
                background: 0x000000,
                shoulderLines: 0xffffff,
                brokenLines: 0xffffff,
                leftCars: [0xd856bf, 0x6750a2, 0xc247ac],
                rightCars: [0x03b3c3, 0x0e5ea5, 0x324555],
                sticks: 0x03b3c3
              }
            }}
          />
        </div>
        <main style={{
          position: 'relative',
          minHeight: '100vh',
          paddingTop: 'calc(2em + 60px + 2rem)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{ color: '#ffffff', marginBottom: '1rem', textAlign: 'center' }}>
              Invalid Join Code
            </h2>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
              {error || 'The join code you entered is invalid or expired.'}
            </p>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <CardNav
        logo="/rollerCoaster_logo.svg"
        logoAlt="Client Roller Coaster Logo"
        logoText="Client Roller Coaster"
        baseColor="#242323"
        menuColor="#fff"
        showBackButton={false}
        showHamburger={false}
        showButton={false}
        items={[]}
      />
      {/* Animated Background */}
      <div 
        className="hyperspeed-animation-container"
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          background: '#000',
          zIndex: 0
        }}
      >
        <Hyperspeed 
          effectOptions={{
            distortion: 'turbulentDistortion',
            length: 400,
            roadWidth: 10,
            islandWidth: 2,
            lanesPerRoad: 4,
            fovSpeedUp: 150,
            speedUp: 0.5,
            fov: 70,
            carLightsFade: 0.4,
            totalSideLightSticks: 20,
            lightPairsPerRoadWay: 40,
            shoulderLinesWidthPercentage: 0.05,
            brokenLinesWidthPercentage: 0.1,
            brokenLinesLengthPercentage: 0.5,
            lightStickWidth: [0.12, 0.5],
            lightStickHeight: [1.3, 1.7],
            movingAwaySpeed: [15, 20],
            movingCloserSpeed: [-30, -40],
            carLightsLength: [400 * 0.03, 400 * 0.2],
            carLightsRadius: [0.05, 0.14],
            carWidthPercentage: [0.3, 0.5],
            carShiftX: [-0.8, 0.8],
            carFloorSeparation: [0, 5],
            colors: {
              roadColor: 0x080808,
              islandColor: 0x0a0a0a,
              background: 0x000000,
              shoulderLines: 0xffffff,
              brokenLines: 0xffffff,
              leftCars: [0xd856bf, 0x6750a2, 0xc247ac],
              rightCars: [0x03b3c3, 0x0e5ea5, 0x324555],
              sticks: 0x03b3c3
            }
          }}
        />
      </div>
      
      <main style={{
        position: 'relative',
        minHeight: '100vh',
        paddingTop: 'calc(2em + 60px + 2rem)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        zIndex: 1
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          width: '100%',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <h2 style={{ color: '#ffffff', marginBottom: '0.5rem', textAlign: 'center', fontSize: '1.5rem' }}>
            Join Game
          </h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', marginBottom: '2rem' }}>
            {game.game_name}
          </p>

          {/* Show loading message if auto-joining */}
          {autoJoining && (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                Joining game...
              </div>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                Please wait while we add you to the game.
              </div>
            </div>
          )}

          {/* Only show form if user is NOT authenticated */}
          {!user && !autoJoining && (
          <form onSubmit={handleJoin}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: 500
              }}>
                Your Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  color: '#ffffff',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#D702F6';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: 500
              }}>
                Email Address *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  color: '#ffffff',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#D702F6';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
              />
            </div>

            {error && (
              <div style={{
                background: 'rgba(255, 59, 48, 0.1)',
                border: '1px solid rgba(255, 59, 48, 0.3)',
                borderRadius: '6px',
                padding: '0.75rem',
                marginBottom: '1.5rem',
                color: '#FF3B30',
                fontSize: '0.875rem'
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !name.trim() || !email.trim()}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: (loading || !name.trim() || !email.trim()) 
                  ? 'rgba(215, 2, 246, 0.3)' 
                  : '#D702F6',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: (loading || !name.trim() || !email.trim()) 
                  ? 'not-allowed' 
                  : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: (loading || !name.trim() || !email.trim()) 
                  ? 'none' 
                  : '0 4px 15px rgba(215, 2, 246, 0.4)',
                opacity: (loading || !name.trim() || !email.trim()) 
                  ? 0.6 
                  : 1
              }}
              onMouseEnter={(e) => {
                if (!loading && name.trim() && email.trim()) {
                  e.target.style.background = '#C001E5';
                  e.target.style.boxShadow = '0 6px 20px rgba(215, 2, 246, 0.6)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && name.trim() && email.trim()) {
                  e.target.style.background = '#D702F6';
                  e.target.style.boxShadow = '0 4px 15px rgba(215, 2, 246, 0.4)';
                }
              }}
            >
              {loading ? 'Joining...' : 'Join Game'}
            </button>
          </form>
          )}
        </div>
      </main>
    </>
  );
}

export default JoinGamePage;

